import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, X, Sparkles, Loader2, Check, RefreshCw } from "lucide-react";

const TONES = [
  { value: "friendly", label: "Friendly", hint: "Warm, growth-cue phrasing" },
  { value: "neutral", label: "Neutral", hint: "Direct, coach's own words" },
];

/**
 * SendRecapModal — shared modal for turning any coach note (voice-dictated or
 * typed) into a player-facing recap sent via email + in-app notification.
 *
 * Flow:
 *   1. Opened with `sourceBody` — the coach's private note text
 *   2. Calls /recap/preview with the chosen tone → Claude rewrites into a
 *      short message addressed to the player
 *   3. Coach edits the rewritten text if desired (or toggles tone → re-preview)
 *   4. Send → /recap/send delivers email (Resend) + in-app notification
 */
export default function SendRecapModal({
  open,
  onClose,
  studentId,
  studentName = "",
  sourceBody = "",
  noteId = "",
}) {
  const [tone, setTone] = useState("friendly");
  const [subject, setSubject] = useState("");
  const [preview, setPreview] = useState(null); // {recap, tone, coach_first_name, player_first_name}
  const [editable, setEditable] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  // Fresh preview every time the modal opens (or the tone changes, on demand).
  useEffect(() => {
    if (!open) {
      setPreview(null); setEditable(""); setSubject(""); setPreviewing(false); setSending(false); setTone("friendly");
      return;
    }
    if (sourceBody && !preview) runPreview("friendly");
     
  }, [open, sourceBody]);

  if (!open) return null;

  const runPreview = async (nextTone) => {
    if (!sourceBody?.trim()) { toast.error("Nothing to recap yet."); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post(`/coach/players/${studentId}/recap/preview`, {
        body: sourceBody,
        tone: nextTone,
      });
      setPreview(data);
      setEditable(data.recap);
      setTone(data.tone);
      if (!subject) {
        setSubject(`Recap from Coach ${data.coach_first_name} — your next focus`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed — try again.");
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    const trimmed = (editable || "").trim();
    if (trimmed.length < 10) { toast.error("Recap is too short to send."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/coach/players/${studentId}/recap/send`, {
        body: trimmed,
        subject: subject || undefined,
        tone,
        note_id: noteId || undefined,
      });
      // Distinguish partial-success (notification only) from full success (email + notif).
      if (data.email_sent) {
        toast.success(`Sent to ${data.delivered_to?.name || "player"} — email + in-app`);
      } else if (data.notification_sent) {
        toast.warning(`Only in-app notification sent (email failed): ${data.email_error || "unknown"}`);
      } else {
        toast.error("Delivery failed — please try again.");
      }
      // Nudge the on-page recap history to reload so it appears immediately.
      window.dispatchEvent(new CustomEvent("recaps:changed", { detail: { studentId } }));
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="send-recap-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-[#CCFF00]" />
            <div>
              <div className="text-sm font-medium">Send recap to player</div>
              {studentName && <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">for {studentName}</div>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#141414]" data-testid="recap-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Tone toggle + regenerate */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="label-mono text-[10px]">Tone</div>
            <div className="inline-flex items-center gap-1 border border-[#262626] bg-[#0A0A0A]">
              {TONES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => runPreview(t.value)}
                  disabled={previewing || sending}
                  className={`text-[10px] uppercase tracking-wider px-3 py-1.5 transition-colors ${
                    tone === t.value ? "text-[#CCFF00]" : "text-[#525252] hover:text-[#A3A3A3]"
                  }`}
                  title={t.hint}
                  data-testid={`recap-tone-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => runPreview(tone)}
              disabled={previewing || sending}
              className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="recap-regenerate"
            >
              {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {previewing ? "Rewriting…" : "Regenerate"}
            </button>
          </div>

          {/* Source note (compact, always visible so coach knows what it's from) */}
          <div>
            <div className="label-mono text-[10px] mb-1">Source note</div>
            <div className="text-xs text-[#A3A3A3] italic border-l-2 border-[#262626] pl-3 leading-relaxed max-h-24 overflow-y-auto" data-testid="recap-source">
              {sourceBody}
            </div>
          </div>

          {/* Subject line (email only — notification uses this too as the title) */}
          <div>
            <div className="label-mono text-[10px] mb-1">Subject</div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={120}
              className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
              placeholder="Recap from Coach…"
              data-testid="recap-subject"
            />
          </div>

          {/* Rewritten message — editable */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="label-mono text-[10px] inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#CCFF00]" /> Recap message
              </div>
              {previewing && !preview && (
                <div className="text-[10px] text-[#A3A3A3] inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Rewriting…
                </div>
              )}
            </div>
            <textarea
              value={editable}
              onChange={(e) => setEditable(e.target.value)}
              rows={8}
              maxLength={2000}
              placeholder={previewing ? "" : "Recap will appear here…"}
              className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none leading-relaxed"
              data-testid="recap-editable"
            />
            <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-1">
              {editable.length} / 2000 · sends via email + in-app notification
            </div>
          </div>
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-between">
          <div className="text-[10px] text-[#A3A3A3]">
            Player sees whichever they open first
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="text-xs px-4 py-2 border border-[#262626] hover:border-[#525252]"
              data-testid="recap-cancel"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={sending || previewing || (editable || "").trim().length < 10}
              className="text-xs px-4 py-2 bg-[#CCFF00] text-[#0A0A0A] font-semibold disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="recap-send"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {sending ? "Sending…" : "Send to player"}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
